import { DateTimeHelper } from '../../../helpers/date-time-helper';
import { transient, autoinject } from "aurelia-dependency-injection";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { DataGridColumn, DataType } from '../../../models/queries/queries.models';
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import * as Enumerable from 'linq';
import { observable } from 'aurelia-binding';

@autoinject
@transient()
export class ManageColumnsDialog {

    model: { columns: DataGridColumn[], reloadData: () => void, onSaveManageColumns: () => void };
    columns: DataGridColumn[];
    availableColumns: DataGridColumn[];
    availableSorting: DataGridColumn[];
    availableGrouping: DataGridColumn[];
    visibleColumns: DataGridColumn[];
    visibleSorting: DataGridColumn[];
    visibleGrouping: DataGridColumn[];
    selectedAvailableColumn: DataGridColumn;
    selectedVisibleColumn: DataGridColumn;

    @observable({ changeHandler: 'resetColumns' })
    availableColumnsQuery: string;

    @observable({ changeHandler: 'resetColumns' })
    visibleColumnsQuery: string;

    @observable({ changeHandler: 'resetColumns' })
    availableSortingQuery: string;

    @observable({ changeHandler: 'resetColumns' })
    visibleSortingQuery: string;

    @observable({ changeHandler: 'resetColumns' })
    availableGroupingQuery: string;

    @observable({ changeHandler: 'resetColumns' })
    visibleGroupingQuery: string;

    constructor(private dialogClientService: DialogClientService) { }

    activate(model: { columns: DataGridColumn[], reloadData: () => void, onSaveManageColumns: () => void }) {
        if (model) {
            this.model = model;
            this.columns = CloneHelper.cloneObject(model.columns);
            this.resetColumns();
        }
    }

    public selected = "panel-columns";

    select(item: string) {
        this.selected = item;
    }

    save() {
        this.columns.forEach(column => {
            if ((column.dataType === DataType.Date || column.dataType === DataType.DateTime) && !column.valueConverter) {
                column.valueConverter = (value) => value ? DateTimeHelper.getFormattedDate(new Date(value)) + " " + (new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })) : "";
            }
        });

        // Remove all columns from models and push the cloned ones to force an update of them in the Caller Element
        this.model.columns.splice(0, this.columns.length);
        this.model.columns.push(...this.columns);

        if (typeof (this.model.reloadData) == "function") {
            this.model.reloadData();
        }
        if (typeof (this.model.onSaveManageColumns) == "function") {
            this.model.onSaveManageColumns();
        }
        this.dialogClientService.hide();
    }

    cancel() {
        this.dialogClientService.hide();
    }

    setSelectedAvailableColumn(column: DataGridColumn) {
        this.selectedAvailableColumn = column;
    }

    setSelectedVisibleColumn(column: DataGridColumn) {
        this.selectedVisibleColumn = column;
    }

    moveColumnRight(column: DataGridColumn) {
        if (column) {
            column.isHidden = false;
        }
        this.resetColumns();
    }

    moveSortingRight(column: DataGridColumn) {
        if (column) {
            column.isSortable = true;
            column.isAscending = true;
        }
        this.resetColumns();
    }

    moveGroupingRight(column: DataGridColumn) {
        if (column) {
            column.isGrouping = true;
        }
        this.resetColumns();
    }

    moveColumnLeft(column: DataGridColumn) {
        if (column) {
            column.isHidden = true;
        }
        this.resetColumns();
    }

    moveSortingLeft(column: DataGridColumn) {
        if (column) {
            column.isSortable = false;
        }
        this.resetColumns();
    }

    moveGroupingLeft(column: DataGridColumn) {
        if (column) {
            column.isGrouping = false;
        }
        this.resetColumns();
    }

    public moveSortingDown() {
        if (this.selectedVisibleColumn) {
            this.fixSortOrder();
            const list = Enumerable.from(this.visibleSorting);
            if (this.selectedVisibleColumn.sortOrder !== (this.visibleSorting.length - 1)) {
                const next = list.first(x => x.sortOrder === (this.selectedVisibleColumn.sortOrder + 1));
                next.sortOrder -= 1;
                this.selectedVisibleColumn.sortOrder += 1;
            }
        }
        this.resetColumns();
    }

    public moveGroupingDown() {
        if (this.selectedVisibleColumn) {
            this.fixGroupOrder();
            const list = Enumerable.from(this.visibleGrouping);
            if (this.selectedVisibleColumn.groupOrder !== (this.visibleGrouping.length - 1)) {
                const next = list.first(x => x.groupOrder === (this.selectedVisibleColumn.groupOrder + 1));
                next.groupOrder -= 1;
                this.selectedVisibleColumn.groupOrder += 1;
            }
        }
        this.resetColumns();
    }

    public moveSortingUp() {
        if (this.selectedVisibleColumn) {
            this.fixSortOrder();
            const list = Enumerable.from(this.visibleSorting);
            if (this.selectedVisibleColumn.sortOrder !== 0) {
                const prev = list.first(x => x.sortOrder === (this.selectedVisibleColumn.sortOrder - 1));
                prev.sortOrder += 1;
                this.selectedVisibleColumn.sortOrder -= 1;
            }
        }
        this.resetColumns();
    }

    public moveGroupingUp() {
        if (this.selectedVisibleColumn) {
            this.fixGroupOrder();
            const list = Enumerable.from(this.visibleGrouping);
            if (this.selectedVisibleColumn.groupOrder !== 0) {
                const prev = list.first(x => x.groupOrder === (this.selectedVisibleColumn.groupOrder - 1));
                prev.groupOrder += 1;
                this.selectedVisibleColumn.groupOrder -= 1;
            }
        }
        this.resetColumns();
    }

    moveColumnUp() {
        if (this.selectedVisibleColumn) {
            this.fixColumnOrder();
            const list = Enumerable.from(this.visibleColumns);
            if (this.selectedVisibleColumn.columnOrder !== 0) {
                const prev = list.first(x => x.columnOrder === (this.selectedVisibleColumn.columnOrder - 1));
                prev.columnOrder += 1;
                this.selectedVisibleColumn.columnOrder -= 1;
            }
        }
        this.resetColumns();
    }

    moveColumnDown() {
        if (this.selectedVisibleColumn) {
            this.fixColumnOrder();
            const list = Enumerable.from(this.visibleColumns);
            if (this.selectedVisibleColumn.columnOrder !== (this.visibleColumns.length - 1)) {
                const next = list.first(x => x.columnOrder === (this.selectedVisibleColumn.columnOrder + 1));
                next.columnOrder -= 1;
                this.selectedVisibleColumn.columnOrder += 1;
            }
        }
        this.resetColumns();
    }

    fixColumnOrder() {
        for (const x of this.visibleColumns) {
            x.columnOrder = this.visibleColumns.indexOf(x);
        }
    }

    fixSortOrder() {
        for (const x of this.visibleSorting) {
            x.sortOrder = this.visibleSorting.indexOf(x);
        }
    }

    fixGroupOrder() {
        for (const x of this.visibleGrouping) {
            x.groupOrder = this.visibleGrouping.indexOf(x);
        }
    }

    resetColumns() {
        const enumerable = Enumerable.from(this.columns);
        this.availableColumns = enumerable.where(x => typeof x.isHidden === "function" ? true : x.isHidden && this.containsQuery(x, this.availableColumnsQuery)).orderBy(x => x.columnOrder).toArray();
        this.availableSorting = enumerable.where(x => !x.isSortable && this.containsQuery(x, this.availableSortingQuery)).orderBy(x => x.sortOrder).toArray();
        this.availableGrouping = enumerable.where(x => !x.isGrouping && this.containsQuery(x, this.availableGroupingQuery)).orderBy(x => x.groupOrder).toArray();
        this.visibleColumns = enumerable.where(x => !x.isHidden && this.containsQuery(x, this.visibleColumnsQuery)).orderBy(x => x.columnOrder).toArray();
        this.visibleSorting = enumerable.where(x => x.isSortable && this.containsQuery(x, this.visibleSortingQuery)).orderBy(x => x.sortOrder).toArray();
        this.visibleGrouping = enumerable.where(x => x.isGrouping && this.containsQuery(x, this.visibleGroupingQuery)).orderBy(x => x.groupOrder).toArray();
    }

    public containsQuery(x: DataGridColumn, query: string) {
        return (!query || x.title.trim().toLowerCase().indexOf(query.trim().toLowerCase()) != -1);
    }
}
